import { FC, InputHTMLAttributes, forwardRef } from 'react'
import { Lock, Eye, EyeOff } from 'lucide-react'

/**
 * PasswordInput component props
 */
interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text for the input */
  label: string
  /** Whether password is visible */
  showPassword: boolean
  /** Toggle password visibility */
  onToggleVisibility: () => void
  /** Optional right-side content (e.g., "Forgot password?" link) */
  labelRight?: React.ReactNode
}

/**
 * PasswordInput - Password field with show/hide toggle and lock icon
 *
 * @example
 * ```tsx
 * const [showPassword, setShowPassword] = useState(false)
 *
 * <PasswordInput
 *   label="Password"
 *   showPassword={showPassword}
 *   onToggleVisibility={() => setShowPassword(!showPassword)}
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 * />
 * ```
 */
export const PasswordInput: FC<PasswordInputProps> = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ label, showPassword, onToggleVisibility, labelRight, className = '', ...inputProps }, ref) => {
    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            htmlFor={inputProps.id}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {label}
          </label>
          {labelRight}
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="w-5 h-5 text-gray-400" />
          </div>
          <input
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            className={`w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-[#555555] bg-white dark:bg-[#3A3A3A] text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${className}`}
            {...inputProps}
          />
          <button
            type="button"
            onClick={onToggleVisibility}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
